/**
 * Photo-picks IndexedDB store — Befund 5 (Sebastian ruling 2026-08-12).
 *
 * File-object persistence for the Schritt 1 · Personen upload slots.
 * Pre-fix, photos lived in `PersonenRoute`'s transient
 * `useState<Map<castKey, CastPhotos>>` and were discarded on a page
 * reload (per Sebastian's original 2026-08-06 ruling
 * `[book-photos-no-preauth-upload]`, now SUPERSEDED — the reload gap
 * kept trapping customers mid-flow and had to close).
 *
 * ═══ WHY IDB, NOT LOCALSTORAGE ═════════════════════════════════════
 *
 * `localStorage` stringifies values; a `File` (a `Blob` subclass) is
 * NOT serializable to JSON in any lossless way. IDB uses the
 * structured-clone algorithm, which supports `File`/`Blob` natively —
 * a File read back from IDB round-trips with its type, name, size,
 * and byte contents intact.
 *
 * ═══ WHY NO idb LIBRARY ══════════════════════════════════════════════
 *
 * The `idb` npm package would be one dep + one API surface for a tiny
 * three-method use case (get all, put one, clear all). The vanilla
 * IDB API is verbose but stable; a ~130-line wrapper keeps the
 * dependency graph small and the failure modes visible.
 *
 * ═══ LIFECYCLE ═══════════════════════════════════════════════════════
 *
 * Writes fire on every photo pick / remove in PersonenRoute. The
 * whole store clears on Light-Gate success (`clearAll`) — once the
 * photos are on the server, IDB is redundant AND carries PII
 * (children's photos). Never keep post-upload copies.
 *
 * Errors are logged and swallowed to avoid blocking the customer:
 * a browser with IDB blocked (private mode + Firefox pre-2025 or
 * corporate lockdown) still lets the customer pick photos and use
 * the flow within the session; only the reload-survivability degrades.
 *
 * ═══ KEY SHAPE ══════════════════════════════════════════════════════
 *
 * Rows keyed by `castKey` (from the Konfigurator store's cast entry).
 * The same cast keys survive `book-configurator-store` persistence
 * (localStorage), so an IDB row keyed by `castKey='abc'` still
 * matches a cast entry with `key='abc'` after a reload.
 *
 * Row shape: `{ castKey: string, face?: File }`.
 * A cast entry with no face photo has no row (never a row of all-null).
 *
 * ═══ LEGACY ROWS ═══════════════════════════════════════════════════
 *
 * Pre-fix drafts may carry an extra `fullBody?: File` key on their
 * rows (the second-photo slot that was removed with
 * `fix/single-photo`). Structured-clone persistence preserves the
 * property even after the TypeScript shape narrows. `getAllPhotoPicks`
 * still returns such rows safely — callers must simply read `face`
 * only. `PersonenRoute` additionally fires a one-shot cleanup via
 * `putPhotoPick(castKey, 'face', row.face ?? null)` on hydration so
 * the stale key is pruned on first read. Fire-and-forget; the hydration
 * does not await it.
 */

/** Storage DB name. */
const DB_NAME = 'dreamable-book-photo-picks';
const DB_VERSION = 1;
const STORE_NAME = 'photos';

export interface PhotoPickRow {
  readonly castKey: string;
  readonly face?: File;
}

/**
 * Open the IDB database and initialize the object store on first use.
 * Wrapped in a promise; consumers await this once per operation.
 * The connection is cheap to reopen; no shared singleton needed at
 * this scale (one book flow, up to 16 photos).
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('photo-picks-idb: indexedDB unavailable in this runtime'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'castKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('photo-picks-idb: open failed'));
  });
}

/**
 * Fetch every persisted row. Consumers rebuild their in-memory map
 * from the returned rows on mount. Empty on a fresh browser session
 * or after a `clearAll`.
 */
export async function getAllPhotoPicks(): Promise<readonly PhotoPickRow[]> {
  const db = await openDb();
  try {
    return await new Promise<readonly PhotoPickRow[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as PhotoPickRow[]);
      req.onerror = () => reject(req.error ?? new Error('photo-picks-idb: getAll failed'));
    });
  } finally {
    db.close();
  }
}

/**
 * Write the face pick for `castKey`. `file: null` clears the slot;
 * if the resulting row has no face either, the row is deleted.
 *
 * Also prunes any legacy `fullBody` key: the write constructs a row
 * from `{castKey, face?}` only, so a legacy row that still carried a
 * `fullBody: File` after the second-photo slot was removed loses
 * that property on the first write.
 */
export async function putPhotoPick(
  castKey: string,
  slot: 'face',
  file: File | null,
): Promise<void> {
  // `slot` is retained in the signature for call-site readability and
  // for test fixtures that pre-date `fix/single-photo`. Narrowed to
  // the literal 'face'; a future second slot would re-expand it.
  void slot;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      // Conditional spread so that under exactOptionalPropertyTypes
      // the `face?: File` property is OMITTED (not set to undefined)
      // when the slot is cleared.
      const next: PhotoPickRow = {
        castKey,
        ...(file !== null ? { face: file } : {}),
      };
      if (next.face === undefined) {
        store.delete(castKey);
      } else {
        store.put(next);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('photo-picks-idb: put failed'));
      tx.onabort = () => reject(tx.error ?? new Error('photo-picks-idb: put aborted'));
    });
  } finally {
    db.close();
  }
}

/**
 * Delete every row for a cast entry (both slots). Called when the
 * customer removes a cast entry from the roster.
 */
export async function clearPhotoPicksFor(castKey: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(castKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('photo-picks-idb: clearFor failed'));
    });
  } finally {
    db.close();
  }
}

/**
 * Wipe every row in the store. Fires after Light-Gate success: the
 * photos are now on the server via signed-URL upload, IDB is
 * redundant AND carries children's photos as PII. Never keep
 * post-upload copies. Swallows errors — a failing clearAll doesn't
 * block the post-success navigation, but the next Personen visit
 * will hydrate stale entries; the store's `put` overwrites are
 * idempotent so this degrades gracefully.
 */
export async function clearAllPhotoPicks(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('photo-picks-idb: clearAll failed'));
    });
  } finally {
    db.close();
  }
}
